import styled from 'styled-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import Lottie from 'lottie-react';
import { MoreHorizontal } from 'lucide-react';
import Card from '../../components/Card';
import PortalAvatar from '../profile/PortalAvatar';
import fistBump from '../../assets/fistBump.json';
import { useAuth } from '../../context/AuthProvider';
import {
  CLIENT_ORG_ROLE_LABELS,
  type ClientOrgInvite,
  type ClientOrgMember,
  fetchClientOrgInvites,
  fetchClientOrgMembers,
  removeClientOrgMember,
} from '../../lib/clientOrganization';
import {
  CLIENT_ORG_INVITE_CREATED_EVENT,
  type ClientOrgInviteCreatedEventDetail,
} from '../../lib/clientOrganizationEvents';

const Shell = styled(Card)`
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 300px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
`;

const HeaderMain = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: .75rem;
`;

const TitleMark = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  overflow: hidden;
  flex: 0 0 auto;
  background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.04));
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
  display: grid;
  place-items: center;
`;

const TitleMarkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const TitleMarkFallback = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: #e8eaed;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .06em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 0;
  font-weight: 900;
  color: #e8eaed;
`;

const InviteHeaderAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #dfe6f2;
  cursor: pointer;

  &:hover {
    color: #f7fbff;
  }
`;

const InviteHeaderLabel = styled.span`
  font-size: .82rem;
  font-weight: 800;
  letter-spacing: .02em;
`;

const InviteHeaderCircle = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  border: 1px dashed rgba(255,255,255,.34);
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
`;

const Body = styled.div`
  position: relative;
  padding-top: .75rem;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Tabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 0.8rem;
`;

const Tab = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: 0;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#E0A15E' : 'transparent')};
  background: transparent;
  color: ${({ $active }) => ($active ? '#E8EAED' : '#9fb0c8')};
  padding: 0.25rem 0.15rem 0.55rem;
  margin-bottom: -1px;
  min-width: 92px;
  font-size: 0.86rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;

  &:hover {
    color: #e8eaed;
  }
`;

const EmptyState = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
  text-align: center;
  color: #aeb7c7;
  gap: .75rem;

  p { margin: 0; }
`;

const ListColumn = styled.div`
  display: grid;
  gap: .55rem;
  align-content: start;
  width: 100%;
`;

const TileBase = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .85rem;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.03);
  padding: .75rem .85rem;
  color: #e8eaed;
  min-width: 0;
  width: 100%;
`;

const MemberTile = styled(TileBase) <{ $pending?: boolean; $interactive?: boolean }>`
  position: relative;
  width: 100%;
  appearance: none;
  text-align: left;
  font: inherit;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  opacity: ${({ $pending }) => ($pending ? 0.58 : 1)};
  filter: ${({ $pending }) => ($pending ? 'saturate(.65)' : 'none')};

  &:hover {
    background: ${({ $interactive }) => ($interactive ? 'rgba(255,255,255,.06)' : 'rgba(255,255,255,.03)')};
    border-color: ${({ $interactive }) => ($interactive ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.08)')};
  }
`;

const PendingPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: .05rem .35rem;
  border-radius: 999px;
  border: 1px dashed rgba(255,255,255,.25);
  color: #9fb0c8;
  font-size: .65rem;
  font-weight: 800;
  letter-spacing: .03em;
  text-transform: uppercase;
`;

const MemberMain = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: .75rem;
`;

const Avatar = styled(PortalAvatar)`
  flex: 0 0 auto;
`;

const EmailBlock = styled.div`
  min-width: 0;
  width: 100%;
  display: grid;
  gap: .1rem;

  & > span {
    color: #9da7b8;
    font-size: .88rem;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NameRow = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: .35rem;

  & > strong {
    min-width: 0;
    flex: 1 1 auto;
    display: inline-block;
    font-weight: 900;
    color: #e8eaed;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ActionWrap = styled.div`
  flex: 0 0 auto;
`;

const DotsAction = styled.button`
  width: 30px;
  height: 20px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,.16);
  background: rgba(255,255,255,.04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,.1);
    color: #e8eaed;
  }
`;

const Menu = styled.div`
  position: fixed;
  width: 156px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(9, 14, 26, .98);
  box-shadow: 0 20px 50px rgba(0,0,0,.45);
  z-index: 140;
  overflow: hidden;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: .55rem .65rem;
  border: 0;
  border-bottom: 1px solid rgba(255,255,255,.08);
  background: transparent;
  color: ${({ $danger }) => ($danger ? '#ff9b9b' : '#d9e0ed')};
  font-size: .85rem;
  font-weight: 700;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgba(255,255,255,.08);
  }

  &:disabled {
    cursor: not-allowed;
    color: #7f8898;
    background: transparent;
  }
`;

const ActionNotice = styled.div`
  margin-top: .45rem;
  color: #ff9b9b;
  font-size: .8rem;
  font-weight: 700;
`;

const LottieWrap = styled.div`
  width: 120px;
  height: 120px;
  display: grid;
  place-items: center;
  pointer-events: none;
  margin-bottom: .25rem;

  @media (min-width: 768px) {
    width: 140px;
    height: 140px;
  }

  .lottie { width: 100%; height: 100%; }
`;

type TeamMembersCardProps = {
  onInviteClick?: () => void;
  className?: string;
  title?: string;
  titleLogoUrl?: string | null;
  readOnly?: boolean;
};

type MemberMenuState = {
  uid: string;
  top: number;
  left: number;
};

type TeamTab = 'members' | 'pending';

function memberDisplayName(member: ClientOrgMember): string {
  const firstName = member.firstName?.trim() ?? '';
  const lastName = member.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || member.displayName || member.email || member.uid;
}

function titleInitials(title: string | null | undefined): string {
  const value = String(title ?? '').trim();
  if (!value) return 'OT';
  const letters = value
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 2)
    .toUpperCase();
  return letters || 'OT';
}

function normalizeEmail(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const next = value.trim().toLowerCase();
  return next.length ? next : null;
}

function inviteKey(invite: Pick<ClientOrgInvite, 'token' | 'email' | 'role' | 'orgId'>): string {
  if (typeof invite.token === 'string' && invite.token.trim()) {
    return `token:${invite.token.trim()}`;
  }
  const email = normalizeEmail(invite.email) ?? 'unknown';
  const role = String(invite.role ?? 'member');
  const org = invite.orgId ?? '';
  return `email:${email}|role:${role}|org:${org}`;
}

function inviteDisplayName(invite: ClientOrgInvite): string {
  const email = normalizeEmail(invite.email);
  if (email) return email;
  return 'Pending invite';
}

function inviteSubtitle(invite: ClientOrgInvite): string {
  const roleLabel = CLIENT_ORG_ROLE_LABELS[invite.role] ?? 'Organization member';
  return `Pending invite - ${roleLabel}`;
}

const LOCAL_PENDING_INVITE_TTL_MS = 15 * 60 * 1000;

export default function TeamMembersCard({
  onInviteClick,
  className,
  title,
  titleLogoUrl,
  readOnly = false,
}: TeamMembersCardProps) {
  const { role, orgId, user, isPrimaryAccountHolder, refreshRole } = useAuth();
  const nav = useNavigate();

  const [team, setTeam] = useState<ClientOrgMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<ClientOrgInvite[]>([]);
  const [localPendingInvites, setLocalPendingInvites] = useState<ClientOrgInvite[]>([]);
  const [memberMenu, setMemberMenu] = useState<MemberMenuState | null>(null);
  const [removingUid, setRemovingUid] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TeamTab>('members');

  const shellRef = useRef<HTMLDivElement | null>(null);

  const loadMembers = useCallback(async () => {
    if (role !== 'client') {
      setTeam([]);
      setPendingInvites([]);
      setLocalPendingInvites([]);
      return;
    }
    try {
      const [members, invites] = await Promise.all([
        fetchClientOrgMembers(orgId ?? undefined),
        fetchClientOrgInvites(orgId ?? undefined),
      ]);
      if (members.length === 0 && user && isPrimaryAccountHolder) {
        setTeam([
          {
            uid: user.uid,
            orgId: orgId ?? user.uid,
            email: user.email ?? null,
            displayName: user.displayName ?? null,
            firstName: null,
            lastName: null,
            photoURL: user.photoURL ?? null,
            role: 'owner',
            permissions: {
              inviteMembers: true,
              createTickets: true,
              manageBilling: true,
              manageSubscription: true,
            },
            isPrimaryAccountHolder: true,
          },
        ]);
      } else {
        setTeam(members);
      }
      setPendingInvites(invites.filter((invite) => invite.status === 'pending'));
      setLocalPendingInvites((prev) =>
        prev.filter((invite) => {
          const createdMs = invite.createdAt ? new Date(invite.createdAt).getTime() : Date.now();
          if (Number.isFinite(createdMs) && Date.now() - createdMs > LOCAL_PENDING_INVITE_TTL_MS) {
            return false;
          }
          const serverMatch = invites.some((row) => inviteKey(row) === inviteKey(invite));
          if (serverMatch) return false;
          const email = normalizeEmail(invite.email);
          if (!email) return true;
          return !members.some((member) => normalizeEmail(member.email) === email);
        }),
      );
    } catch {
      setTeam([]);
      setPendingInvites([]);
    }
  }, [isPrimaryAccountHolder, orgId, role, user]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    const onInviteCreated = (event: Event) => {
      const custom = event as CustomEvent<ClientOrgInviteCreatedEventDetail>;
      const invite = custom?.detail?.invite;
      if (!invite) return;
      const scopedOrgId = orgId ?? user?.uid ?? null;
      if (scopedOrgId && invite.orgId && invite.orgId !== scopedOrgId) return;
      setLocalPendingInvites((prev) => {
        const key = inviteKey(invite);
        if (prev.some((row) => inviteKey(row) === key)) return prev;
        return [invite, ...prev];
      });
    };

    window.addEventListener(CLIENT_ORG_INVITE_CREATED_EVENT, onInviteCreated as EventListener);
    return () => {
      window.removeEventListener(CLIENT_ORG_INVITE_CREATED_EVENT, onInviteCreated as EventListener);
    };
  }, [orgId, user?.uid]);

  useEffect(() => {
    const onNotificationsUpdated = () => {
      void loadMembers();
    };
    window.addEventListener('notifications:updated', onNotificationsUpdated);
    return () => {
      window.removeEventListener('notifications:updated', onNotificationsUpdated);
    };
  }, [loadMembers]);

  useEffect(() => {
    if (!memberMenu) return;
    const closeMenu = () => setMemberMenu(null);
    const onDocMouseDown = (event: MouseEvent) => {
      if (!shellRef.current) return;
      if (shellRef.current.contains(event.target as Node)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [memberMenu]);

  const openedMenuMember = useMemo(
    () => team.find((member) => member.uid === memberMenu?.uid) ?? null,
    [team, memberMenu?.uid],
  );

  useEffect(() => {
    if (memberMenu && !openedMenuMember) {
      setMemberMenu(null);
    }
  }, [memberMenu, openedMenuMember]);

  const myMembership = useMemo(
    () => team.find((member) => member.uid === user?.uid) ?? null,
    [team, user?.uid],
  );

  const canManageMembers = Boolean(
    isPrimaryAccountHolder
    || myMembership?.isPrimaryAccountHolder
    || myMembership?.role === 'owner',
  );

  const visiblePendingInvites = useMemo(() => {
    const teamEmails = new Set(
      team
        .map((member) => normalizeEmail(member.email))
        .filter((value): value is string => Boolean(value)),
    );

    const seen = new Set<string>();
    const merged: ClientOrgInvite[] = [];
    const pushInvite = (invite: ClientOrgInvite) => {
      if (invite.status !== 'pending') return;
      const email = normalizeEmail(invite.email);
      if (email && teamEmails.has(email)) return;
      const key = inviteKey(invite);
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(invite);
    };

    pendingInvites.forEach(pushInvite);
    localPendingInvites.forEach(pushInvite);

    return merged.sort((a, b) => {
      const aMs = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bMs = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bMs - aMs;
    });
  }, [localPendingInvites, pendingInvites, team]);

  const visibleTeamMembers = useMemo(() => team, [team]);

  const hasDisplayRows = readOnly
    ? visibleTeamMembers.length > 0
    : visibleTeamMembers.length + visiblePendingInvites.length > 0;
  const showMembersTab = readOnly || activeTab === 'members';
  const activeRows = showMembersTab ? visibleTeamMembers : visiblePendingInvites;

  const goInvite = () => {
    if (onInviteClick) {
      onInviteClick();
      return;
    }
    nav('/dashboard/profile');
  };

  const goMemberProfile = (member: ClientOrgMember) => {
    const selfUid = user?.uid?.trim() ?? '';
    const selfEmail = user?.email?.trim().toLowerCase() ?? '';
    const memberUid = member.uid?.trim() ?? '';
    const memberEmail = normalizeEmail(member.email) ?? '';
    if ((memberUid && memberUid === selfUid) || (memberEmail && memberEmail === selfEmail)) {
      nav('/dashboard/profile');
      return;
    }
    nav(`/dashboard/users/profile/${encodeURIComponent(memberUid || memberEmail)}`);
  };

  const openMemberMenu = (uid: string, anchor: HTMLButtonElement) => {
    const rect = anchor.getBoundingClientRect();
    const menuWidth = 156;
    const menuHeight = 92;
    const viewportPadding = 8;
    const left = Math.max(
      viewportPadding,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - viewportPadding),
    );
    const openUp = window.innerHeight - rect.bottom < menuHeight + viewportPadding;
    const top = openUp
      ? Math.max(viewportPadding, rect.top - menuHeight - 6)
      : rect.bottom + 6;

    setMemberMenu({ uid, top, left });
  };

  const removeMember = async (uid: string) => {
    if (!canManageMembers) return;
    const target = team.find((member) => member.uid === uid);
    if (!target || target.role === 'owner') return;

    try {
      setActionError(null);
      setMemberMenu(null);
      setRemovingUid(uid);
      await removeClientOrgMember(uid, orgId ?? undefined);
      if (uid === user?.uid) {
        await refreshRole();
      }
      await loadMembers();
      if (uid === user?.uid) {
        nav('/dashboard/profile', { replace: true });
      }
    } catch (error: any) {
      setActionError(error?.message || 'Failed to remove member');
    } finally {
      setRemovingUid(null);
    }
  };

  return (
    <Shell ref={shellRef} className={className}>
      <Header>
        <HeaderMain>
          {/* <TitleMark>
            {titleLogoUrl ? (
              <TitleMarkImage src={titleLogoUrl} alt="" />
            ) : (
              <TitleMarkFallback>{titleInitials(title)}</TitleMarkFallback>
            )}
          </TitleMark> */}
          <Title>{title?.trim() || 'Organization Team'}</Title>
        </HeaderMain>
        {!readOnly && canManageMembers ? (
          <InviteHeaderAction type="button" onClick={goInvite} aria-label="Invite organization member">
            <InviteHeaderLabel>Invite</InviteHeaderLabel>
            <InviteHeaderCircle>+</InviteHeaderCircle>
          </InviteHeaderAction>
        ) : null}
      </Header>

      <Body>
        {!readOnly ? (
          <Tabs role="tablist" aria-label="Organization team lists">
            <Tab
              type="button"
              role="tab"
              aria-selected={showMembersTab}
              $active={showMembersTab}
              onClick={() => setActiveTab('members')}
            >
              Members
            </Tab>
            <Tab
              type="button"
              role="tab"
              aria-selected={!showMembersTab}
              $active={!showMembersTab}
              onClick={() => setActiveTab('pending')}
            >
              Pending
            </Tab>
          </Tabs>
        ) : null}

        {!hasDisplayRows ? (
          <EmptyState>
            <LottieWrap>
              <Lottie className="lottie" animationData={fistBump} loop autoplay />
            </LottieWrap>
            <p>
              {!readOnly && canManageMembers
                ? 'Invite members of your business organization'
                : 'No organization members have been added yet.'}
            </p>
          </EmptyState>
        ) : (
          <>
            {activeRows.length === 0 ? (
              <EmptyState>
                <p>
                  {showMembersTab
                    ? 'No members have been added to this organization yet.'
                    : 'No pending invites right now.'}
                </p>
              </EmptyState>
            ) : (
              <ListColumn>
                {showMembersTab
                  ? visibleTeamMembers.map((member) => {
                      const menuOpen = memberMenu?.uid === member.uid;
                      return (
                        <MemberTile
                          key={member.uid}
                          as={readOnly ? 'button' : 'div'}
                          type={readOnly ? 'button' : undefined}
                          $interactive={readOnly}
                          onClick={() => {
                            if (readOnly) {
                              goMemberProfile(member);
                            }
                          }}
                        >
                          <MemberMain>
                            <Avatar
                              uid={member.uid}
                              email={member.email}
                              firstName={member.firstName}
                              lastName={member.lastName}
                              name={memberDisplayName(member)}
                              photoURL={member.photoURL}
                              size={44}
                            />
                            <EmailBlock>
                              <NameRow>
                                <strong>{memberDisplayName(member)}</strong>
                              </NameRow>
                              <span>{CLIENT_ORG_ROLE_LABELS[member.role]}</span>
                            </EmailBlock>
                          </MemberMain>
                          {!readOnly && canManageMembers && !member.isPrimaryAccountHolder && member.role !== 'owner' ? (
                            <ActionWrap>
                              <DotsAction
                                type="button"
                                aria-label="Member actions"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                onClick={(event) => {
                                  if (menuOpen) {
                                    setMemberMenu(null);
                                    return;
                                  }
                                  openMemberMenu(member.uid, event.currentTarget);
                                }}
                              >
                                <MoreHorizontal size={14} />
                              </DotsAction>
                            </ActionWrap>
                          ) : null}
                        </MemberTile>
                      );
                    })
                  : visiblePendingInvites.map((invite) => (
                      <MemberTile key={`pending:${inviteKey(invite)}`} $pending>
                        <MemberMain>
                          <Avatar
                            uid={`pending:${invite.token}`}
                            email={invite.email}
                            firstName={null}
                            lastName={null}
                            name={inviteDisplayName(invite)}
                            photoURL={null}
                            size={44}
                          />
                          <EmailBlock>
                            <NameRow>
                              <strong>{inviteDisplayName(invite)}</strong>
                              <PendingPill>Pending</PendingPill>
                            </NameRow>
                            <span>{inviteSubtitle(invite)}</span>
                          </EmailBlock>
                        </MemberMain>
                      </MemberTile>
                    ))}
              </ListColumn>
            )}

            {actionError ? <ActionNotice>{actionError}</ActionNotice> : null}

            {memberMenu && openedMenuMember
              ? createPortal(
                <Menu role="menu" style={{ top: memberMenu.top, left: memberMenu.left }}>
                  <MenuItem
                    type="button"
                    $danger
                    disabled={
                      !canManageMembers
                      || openedMenuMember.role === 'owner'
                      || removingUid === openedMenuMember.uid
                    }
                    onClick={() => void removeMember(openedMenuMember.uid)}
                  >
                    {removingUid === openedMenuMember.uid ? 'Removing...' : 'Remove'}
                  </MenuItem>
                </Menu>,
                document.body,
              )
              : null}
          </>
        )}
      </Body>
    </Shell>
  );
}
